import React, { forwardRef, useRef } from 'react';
import classNames from 'classnames';
import { useField } from 'formik';

import styles from './DateInput.module.scss';

const {
  'date-input': dateInputClass,
  'date-inputs': dateInputsClass,
  label: labelClass,
  input: inputClass,
  'has-error': hasErrorClass,
  locked: lockedClass,
  focus: forceFocusClass,
  error: errorClass,
  footnote: footnoteClass
} = styles;

const DateInput = forwardRef(
  (
    {
      id,
      locked,
      label,
      dayName,
      dayPlaceholder,
      monthName,
      monthPlaceholder,
      yearName,
      yearPlaceholder,
      footnote,
      customClass,
      storyClass,
      customInputClass,
      customLabelClass,
      customFootnoteClass
    },
    ref
  ) => {
    // useField() returns [formik.getFieldProps(), formik.getFieldMeta()]
    // which we can spread on <input>
    // https://formik.org/docs/api/useField
    // can remove this comment when we get more familiar with Formik
    const dayInput = useRef(null);
    const yearInput = useRef(null);
    const [fieldDay, metaDay] = useField({ name: dayName, type: 'text' });
    const [fieldMonth, metaMonth] = useField({ name: monthName, type: 'text' });
    const [fieldYear, metaYear] = useField({ name: yearName, type: 'text' });

    const inputsHaveError =
      metaMonth.touched &&
      metaDay.touched &&
      metaYear.touched &&
      (metaMonth.error || metaDay.error || metaYear.error);

    const dateInputClasses = classNames(dateInputClass, {
      [customClass]: customClass,
      [lockedClass]: locked,
      [hasErrorClass]: inputsHaveError
    });

    const dateInputsClasses = classNames(dateInputsClass, {
      [forceFocusClass]: storyClass === 'focus'
    });

    const handleAutoFocusToDay = e => {
      if (e?.target?.value?.length === 2) {
        const month = Number(e.target.value);
        if (month >= 1 && month <= 12) {
          dayInput.current.focus();
        }
      }
    };

    const handleAutoFocusToYear = e => {
      if (e?.target?.value?.length === 2) {
        const day = Number(e.target.value);
        if (day >= 1 && day <= 31) {
          yearInput.current.focus();
        }
      }
    };

    return (
      <div className={dateInputClasses}>
        <label className={`${labelClass} ${customLabelClass}`} htmlFor={id || dayName}>
          {label}
        </label>
        <div className={dateInputsClasses}>
          <input
            type="number"
            pattern="[0-9]*"
            ref={ref}
            className={`${inputClass} ${customInputClass}`}
            name={monthName}
            placeholder={monthPlaceholder}
            {...fieldMonth}
            onInput={handleAutoFocusToDay}
          />
          <input
            type="number"
            pattern="[0-9]*"
            ref={dayInput}
            className={`${inputClass} ${customInputClass}`}
            name={dayName}
            placeholder={dayPlaceholder}
            {...fieldDay}
            onInput={handleAutoFocusToYear}
          />
          <input
            type="number"
            pattern="[0-9]*"
            ref={yearInput}
            className={`${inputClass} ${customInputClass}`}
            name={yearName}
            placeholder={yearPlaceholder}
            {...fieldYear}
          />
        </div>
        {inputsHaveError && (
          <div className={errorClass}>
            {[metaMonth.error, metaDay.error, metaYear.error].map(err =>
              !!err ? <div key={err}>{err}</div> : <div></div>
            )}
          </div>
        )}
        {footnote && <div className={`${footnoteClass} ${customFootnoteClass}`}>{footnote}</div>}
      </div>
    );
  }
);

export default DateInput;
